import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faTimes, faPen, faExternalLinkAlt, faCheck, faCopy
} from '@fortawesome/free-solid-svg-icons';

const LinkedInIcon = () => (
    <svg className="lead-linkedin-icon" viewBox="0 0 24 24" fill="currentColor">
        <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.28 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.75M6.46 10.9v8.37H9.2V10.9H6.46M7.83 6.45a1.64 1.64 0 1 0 0 3.28 1.64 1.64 0 0 0 0-3.28z" />
    </svg>
);

const formatLinkedInUrl = (url) => {
    if (!url || !url.trim()) return '';
    const trimmed = url.trim();
    if (/^https?:\/\//i.test(trimmed)) {
        return trimmed;
    }
    if (trimmed.startsWith('www.') || trimmed.startsWith('linkedin.com')) {
        return `https://${trimmed}`;
    }
    return `https://www.linkedin.com/in/${trimmed}`;
};

const formatDate = (isoString) => {
    if (!isoString) return '—';
    try {
        const d = new Date(isoString);
        return d.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    } catch {
        return String(isoString);
    }
};

function AccountDetailModal({ isOpen, onClose, onEdit, account, users = [] }) {
    const [activeTab, setActiveTab] = useState('details');
    const [copiedField, setCopiedField] = useState('');

    if (!isOpen || !account) return null;

    const isOrg = (account.type || account.AccountType) === 'Organization';
    const accountName = account.name || account.accountName || account.AccountName || 'Unnamed Account';
    const contactPerson = account.contactPerson || account.ContactPerson || (isOrg ? '—' : accountName);
    const designation = account.designation || account.ContactDesignation || (isOrg ? 'Key Representative' : 'Individual Practitioner');
    const phone1 = account.phone || account.phone1 || account.CntPhone1 || '';
    const phone2 = account.phone2 || account.CntPhone2 || '';
    const email1 = account.email || account.email1 || account.CntMail1 || '';
    const email2 = account.email2 || account.CntMail2 || '';
    const city = account.city || account.City || '';
    const linkedin = account.linkedin || account.Linkedin || '';
    const status = account.status || account.Status || 'Lead';
    const priority = account.priority || account.Priority || 'Medium';
    const approachStatus = account.approachStatus || account.ApproachStatus || 'New Lead';
    const contactedAt = account.contactedAt || account.ContactedAt;
    const contactMethod = account.contactMethod || account.ContactMethod;
    const contactNotes = account.contactNotes || account.ContactNotes;
    const notes = account.notes || account.Notes || '';
    const recordId = account.id || account.AccountID || '—';
    const createdAt = account.createdAt || account.CreatedAt;
    const updatedAt = account.updatedAt || account.UpdatedAt;

    // Helper to resolve user names
    const resolveUserName = (empId, nameProp) => {
        if (nameProp) return nameProp;
        if (!empId) return '—';
        const found = users.find(u => Number(u.empId) === Number(empId));
        return found ? found.name : `Employee #${empId}`;
    };

    const leadGeneratorName = resolveUserName(account.leadGenerator || account.LeadGenerator, account.leadGeneratorName || account.LeadGeneratorName);
    const responsibleName = resolveUserName(account.responsible || account.Responsible, account.responsibleName || account.ResponsibleName);
    const assignedToName = resolveUserName(account.assignedTo || account.AssignedTo, account.assignedToName || account.AssignedToName);

    const handleCopy = (text, fieldName) => {
        if (!text) return;
        navigator.clipboard.writeText(text);
        setCopiedField(fieldName);
        setTimeout(() => setCopiedField(''), 2000);
    };

    return (
        <div className="slds-modal-backdrop" onClick={onClose}>
            <div className="slds-modal-container" onClick={(e) => e.stopPropagation()}>

                {/* ================= SalesVault LIGHTNING MODAL HEADER ================= */}
                <div className="slds-lightning-header">
                    <div className="slds-record-badge-row">
                        <div className="slds-record-headings">
                            <h2 className="slds-record-title">{accountName}</h2>
                        </div>
                    </div>

                    <div className="slds-header-actions">
                        <button
                            type="button"
                            className="slds-close-icon-btn"
                            onClick={onClose}
                            title="Close"
                        >
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                    </div>
                </div>

                {/* ================= HIGHLIGHTS CARDS ================= */}
                <div className="slds-highlights-card-grid">
                    <div className="slds-highlight-card">
                        <span className="slds-highlight-card-label">Account Type</span>
                        <span className="slds-highlight-card-value" title={isOrg ? 'Organization' : 'Individual'}>
                            {isOrg ? 'Organization' : 'Individual'}
                        </span>
                    </div>
                    <div className="slds-highlight-card">
                        <span className="slds-highlight-card-label">Account Status</span>
                        <span className="slds-highlight-card-value" title={status || '—'}>
                            {status || '—'}
                        </span>
                    </div>
                    <div className="slds-highlight-card">
                        <span className="slds-highlight-card-label">Approach Status</span>
                        <span className="slds-highlight-card-value" title={
                            approachStatus === 'Contacted' ? 'Contacted' :
                                approachStatus === 'Opportunity Created' ? 'Opp Created' :
                                    approachStatus === 'Rejected' ? 'Rejected' : 'New Lead'
                        }>
                            {approachStatus === 'Contacted' ? 'Contacted' :
                                approachStatus === 'Opportunity Created' ? 'Opp Created' :
                                    approachStatus === 'Rejected' ? 'Rejected' : 'New Lead'}
                        </span>
                    </div>
                    <div className="slds-highlight-card">
                        <span className="slds-highlight-card-label">Responsible Person</span>
                        <span className="slds-highlight-card-value" title={responsibleName || '—'}>
                            {responsibleName || '—'}
                        </span>
                    </div>
                    <div className="slds-highlight-card">
                        <span className="slds-highlight-card-label">Location</span>
                        <span className="slds-highlight-card-value" title={city || '—'}>
                            {city || '—'}
                        </span>
                    </div>
                </div>

                {/* ================= LIGHTNING TABS ================= */}
                <div className="slds-tabs-nav">
                    <button
                        type="button"
                        className={`slds-tab-btn ${activeTab === 'details' ? 'active' : ''}`}
                        onClick={() => setActiveTab('details')}
                    >
                        Account Details
                    </button>
                    <button
                        type="button"
                        className={`slds-tab-btn ${activeTab === 'contact' ? 'active' : ''}`}
                        onClick={() => setActiveTab('contact')}
                    >
                        Contact &amp; Outreach
                    </button>
                    <button
                        type="button"
                        className={`slds-tab-btn ${activeTab === 'notes' ? 'active' : ''}`}
                        onClick={() => setActiveTab('notes')}
                    >
                        Requirements &amp; Notes
                    </button>
                    <button
                        type="button"
                        className={`slds-tab-btn ${activeTab === 'system' ? 'active' : ''}`}
                        onClick={() => setActiveTab('system')}
                    >
                        System Info
                    </button>
                </div>

                {/* ================= MODAL BODY / TAB CONTENT ================= */}
                <div className="slds-modal-scroll-body">

                    {/* TAB 1: DETAILS */}
                    {activeTab === 'details' && (
                        <div className="slds-tab-pane">
                            <div className="slds-section-box">
                                <div className="slds-section-header">
                                    <span className="slds-section-title">Account Core Information</span>
                                </div>
                                <table className="dome-detail-table">
                                    <tbody>
                                        <tr>
                                            <th>Account Name</th>
                                            <td><strong>{accountName}</strong></td>
                                            <th>Account Type</th>
                                            <td>{isOrg ? 'Organization / Corporate Entity' : 'Individual Practitioner'}</td>
                                        </tr>
                                        <tr>
                                            <th>Key Contact</th>
                                            <td>{contactPerson || '—'}</td>
                                            <th>Designation</th>
                                            <td>{designation || '—'}</td>
                                        </tr>
                                        <tr>
                                            <th>City / Region</th>
                                            <td>{city || '—'}</td>
                                            <th>Record Status</th>
                                            <td>{status}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            <div className="slds-section-box">
                                <div className="slds-section-header">
                                    <span className="slds-section-title">Ownership &amp; Assignment Details</span>
                                </div>
                                <table className="dome-detail-table">
                                    <tbody>
                                        <tr>
                                            <th>Lead Generator</th>
                                            <td>{leadGeneratorName || '—'}</td>
                                            <th>Responsible Person</th>
                                            <td><strong>{responsibleName || '—'}</strong></td>
                                        </tr>
                                        <tr>
                                            <th>Assigned Representative</th>
                                            <td>{assignedToName || '—'}</td>
                                            <th>Account Priority</th>
                                            <td>{priority}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* TAB 2: CONTACT & OUTREACH */}
                    {activeTab === 'contact' && (
                        <div className="slds-tab-pane">
                            <div className="slds-section-box">
                                <div className="slds-section-header">
                                    <span className="slds-section-title">Telephony &amp; Voice Channels</span>
                                </div>
                                <table className="dome-detail-table">
                                    <tbody>
                                        <tr>
                                            <th>Primary Phone</th>
                                            <td>
                                                {phone1 ? (
                                                    <div className="slds-action-val-group">
                                                        <a href={`tel:${phone1}`} className="slds-link-action">{phone1}</a>
                                                        <button
                                                            type="button"
                                                            className="slds-copy-btn"
                                                            onClick={() => handleCopy(phone1, 'phone1')}
                                                            title="Copy Phone"
                                                        >
                                                            <FontAwesomeIcon icon={copiedField === 'phone1' ? faCheck : faCopy} />
                                                        </button>
                                                    </div>
                                                ) : '—'}
                                            </td>
                                            <th>Secondary Phone</th>
                                            <td>
                                                {phone2 ? (
                                                    <div className="slds-action-val-group">
                                                        <a href={`tel:${phone2}`} className="slds-link-action">{phone2}</a>
                                                        <button
                                                            type="button"
                                                            className="slds-copy-btn"
                                                            onClick={() => handleCopy(phone2, 'phone2')}
                                                            title="Copy Phone"
                                                        >
                                                            <FontAwesomeIcon icon={copiedField === 'phone2' ? faCheck : faCopy} />
                                                        </button>
                                                    </div>
                                                ) : '—'}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            <div className="slds-section-box">
                                <div className="slds-section-header">
                                    <span className="slds-section-title">Email &amp; Digital Channels</span>
                                </div>
                                <table className="dome-detail-table">
                                    <tbody>
                                        <tr>
                                            <th>Primary Email</th>
                                            <td>
                                                {email1 ? (
                                                    <div className="slds-action-val-group">
                                                        <a href={`mailto:${email1}`} className="slds-link-action">{email1}</a>
                                                        <button
                                                            type="button"
                                                            className="slds-copy-btn"
                                                            onClick={() => handleCopy(email1, 'email1')}
                                                            title="Copy Email"
                                                        >
                                                            <FontAwesomeIcon icon={copiedField === 'email1' ? faCheck : faCopy} />
                                                        </button>
                                                    </div>
                                                ) : '—'}
                                            </td>
                                            <th>Secondary Email</th>
                                            <td>
                                                {email2 ? (
                                                    <div className="slds-action-val-group">
                                                        <a href={`mailto:${email2}`} className="slds-link-action">{email2}</a>
                                                        <button
                                                            type="button"
                                                            className="slds-copy-btn"
                                                            onClick={() => handleCopy(email2, 'email2')}
                                                            title="Copy Email"
                                                        >
                                                            <FontAwesomeIcon icon={copiedField === 'email2' ? faCheck : faCopy} />
                                                        </button>
                                                    </div>
                                                ) : '—'}
                                            </td>
                                        </tr>
                                        <tr>
                                            <th>LinkedIn URL</th>
                                            <td colSpan="3">
                                                {linkedin ? (
                                                    <div className="slds-action-val-group">
                                                        <a
                                                            href={formatLinkedInUrl(linkedin)}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="slds-link-action"
                                                        >
                                                            <LinkedInIcon />
                                                            <span style={{ marginLeft: 6 }}>{linkedin}</span>
                                                            <FontAwesomeIcon icon={faExternalLinkAlt} style={{ marginLeft: 6, fontSize: 11 }} />
                                                        </a>
                                                        <button
                                                            type="button"
                                                            className="slds-copy-btn"
                                                            onClick={() => handleCopy(linkedin, 'linkedin')}
                                                            title="Copy LinkedIn URL"
                                                        >
                                                            <FontAwesomeIcon icon={copiedField === 'linkedin' ? faCheck : faCopy} />
                                                        </button>
                                                    </div>
                                                ) : 'No LinkedIn profile recorded'}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            <div className="slds-section-box">
                                <div className="slds-section-header">
                                    <span className="slds-section-title">Outreach &amp; Approaching Status</span>
                                </div>
                                <table className="dome-detail-table">
                                    <tbody>
                                        <tr>
                                            <th>Current Approach Status</th>
                                            <td>
                                                {approachStatus === 'Contacted' ? 'Contacted' :
                                                    approachStatus === 'Opportunity Created' ? 'Opportunity Created' :
                                                        approachStatus === 'Rejected' ? 'Rejected / Dead' : 'New Lead (Pending Outreach)'}
                                            </td>
                                            <th>Contacted Timestamp</th>
                                            <td>{contactedAt ? formatDate(contactedAt) : 'Not contacted yet'}</td>
                                        </tr>
                                        {contactMethod && (
                                            <tr>
                                                <th>Contact Method</th>
                                                <td colSpan="3">{contactMethod}</td>
                                            </tr>
                                        )}
                                        {contactNotes && (
                                            <tr>
                                                <th>Outreach Notes</th>
                                                <td colSpan="3">{contactNotes}</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* TAB 3: REQUIREMENTS & NOTES */}
                    {activeTab === 'notes' && (
                        <div className="slds-tab-pane">
                            <div className="slds-section-box">
                                <div className="slds-section-header">
                                    <span className="slds-section-title">Account Requirements &amp; Engagement Summary</span>
                                </div>
                                <table className="dome-detail-table">
                                    <tbody>
                                        <tr>
                                            <th style={{ width: '22%' }}>Requirements &amp; Clinical Notes</th>
                                            <td>
                                                {notes || 'No requirement notes recorded. Edit the account to add client requirements or clinical details.'}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* TAB 4: SYSTEM INFO */}
                    {activeTab === 'system' && (
                        <div className="slds-tab-pane">
                            <div className="slds-section-box">
                                <div className="slds-section-header">
                                    <span className="slds-section-title">Audit Trail &amp; System Identifiers</span>
                                </div>
                                <table className="dome-detail-table">
                                    <tbody>
                                        <tr>
                                            <th>SalesVault Record ID</th>
                                            <td><code>ACC-{recordId}</code></td>
                                            <th>Database Primary Key</th>
                                            <td><code>#{recordId}</code></td>
                                        </tr>
                                        <tr>
                                            <th>Record Created At</th>
                                            <td>{formatDate(createdAt)}</td>
                                            <th>Last Modified At</th>
                                            <td>{formatDate(updatedAt || createdAt)}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                </div>

                {/* ================= SalesVault LIGHTNING MODAL FOOTER ================= */}
                <div className="slds-lightning-footer">

                    <div className="slds-footer-actions">
                        <button
                            type="button"
                            className="slds-btn slds-btn-neutral"
                            onClick={onClose}
                        >
                            Close
                        </button>
                        {onEdit && (
                            <button
                                type="button"
                                className="slds-btn slds-btn-brand"
                                onClick={() => {
                                    onClose();
                                    onEdit(account);
                                }}
                            >
                                <FontAwesomeIcon icon={faPen} className="mr-6px" />
                                Edit Account Details
                            </button>
                        )}
                    </div>
                </div>

            </div>
        </div>
    );
}

export default AccountDetailModal;
